import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useDomain, DOMAINS } from '../../contexts/DomainContext';

const DashboardTitle = ({ 
  icon: Icon, 
  title, 
  subtitle, 
  actions,
  size = 'medium' // 'small', 'medium', 'large'
}) => {
  const theme = useTheme();
  const { domain } = useDomain();
  const isDarkMode = theme.palette.mode === 'dark';

  // 도메인별 색상 설정
  const getTextColor = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? '#f0e6d9' : 'rgba(0, 0, 0, 0.87)';
    }
    return isDarkMode ? '#b3c5e6' : 'rgba(0, 0, 0, 0.87)';
  };

  const getAccentColor = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? '#f4a261' : '#e76f51';
    }
    return isDarkMode ? '#64b5f6' : '#1976d2';
  };

  // 크기별 설정 - 반응형 지원
  const getSizeConfig = () => {
    switch (size) {
      case 'small':
        return {
          titleVariant: 'h6',
          iconSize: { xs: 16, sm: 18, md: 20 },
          titleWeight: 600,
          subtitleVariant: 'caption',
          spacing: 1,
          titleFontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' }
        };
      case 'large':
        return {
          titleVariant: 'h4',
          iconSize: { xs: 24, sm: 28, md: 32 },
          titleWeight: 700,
          subtitleVariant: 'body1',
          spacing: 2,
          titleFontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
        };
      default: // medium
        return {
          titleVariant: 'h5',
          iconSize: { xs: 20, sm: 22, md: 24 },
          titleWeight: 600,
          subtitleVariant: 'body2',
          spacing: 1.5,
          titleFontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem' }
        };
    }
  };

  const sizeConfig = getSizeConfig();

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      mb: sizeConfig.spacing,
      position: 'relative'
    }}>
      {/* Left: Icon + Title + Subtitle */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
        {Icon && (
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: { xs: 24, sm: 26, md: 28 },
            height: { xs: 24, sm: 26, md: 28 },
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${getAccentColor()}20 0%, ${getAccentColor()}10 100%)`,
            border: `1px solid ${getAccentColor()}30`,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              background: `linear-gradient(135deg, transparent 0%, ${getAccentColor()}08 100%)`,
              pointerEvents: 'none'
            }
          }}>
            <Icon sx={{ 
              color: getAccentColor(), 
              fontSize: sizeConfig.iconSize,
              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
            }} />
          </Box>
        )}
        
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant={sizeConfig.titleVariant} 
            sx={{ 
              fontWeight: sizeConfig.titleWeight,
              fontSize: sizeConfig.titleFontSize,
              color: getTextColor(),
              background: `linear-gradient(135deg, ${getAccentColor()} 0%, ${getAccentColor()}CC 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              position: 'relative',
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
              whiteSpace: 'normal',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              lineHeight: 1.2,
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -2,
                left: 0,
                right: 0,
                height: '2px',
                background: `linear-gradient(90deg, ${getAccentColor()}40 0%, transparent 100%)`,
                borderRadius: '1px'
              }
            }}
          >
            {title}
          </Typography>
          
          {subtitle && (
            <Typography 
              variant={sizeConfig.subtitleVariant} 
              sx={{ 
                color: getTextColor() + '99',
                mt: 0.5,
                fontWeight: 400
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Right: Actions */}
      {actions && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
          {actions}
        </Box>
      )}
    </Box>
  );
};

export default DashboardTitle;